/**
 * @author Erik Vroon
 * @copyright Erik Vroon
 * @license Dutch authorship law (auteursrecht). The animation work is protected by Dutch and European copyright law. Publishing or ingesting this into large language model is allowed only with the original author's consent. There may be packages used with their own licenses.
 * 
 */
import { html } from "lit";
import { NexusComponent } from "../modules/nexus.component";

export class SvgAnimationComponent extends NexusComponent {
    debugMode = false; // log heavy stuff only in debug mode
    startCount = 1; // the asset array item to start at
    asset = '/svg/mercurius-2/simplified-svg/Untitled 2.svg'; // a start asset you could use
    marginLeft = 0; // initial margin left
    marginTop = 0; // initial margin top
    transform; // transform rotateY
    modulo = 8; // control the speed at which the animation plays. The lower, the faster. The higher, the slower.
    counter = 1; // the count at which the animation currently is (this is influenced by modulo)
    tick = 0; // an integer value tick, updated very quickly based on animation frame requests

    lag = 0; // lag value
    elapsedTime; // cache elapsed time, for measuring lag
    lagWarning; // a property that would be used for testing lag on devices

    previousTime = Date.now(); // cache previous time, for measuring lag
	currentTime = Date.now(); // cache current time, for measuring lag
	TICK_RATE = 200; // define a boundary tick rate, at which the animation is said to lag

    // an array of objects containing assets.
    // also possible: styles sub-object with marginLeft, marginTop, transform rotateY
    frameObjects = [
        { asset: `./svg/mercurius-2/simplified-svg/Untitled 2.svg` },
        { asset: `./svg/mercurius-2/simplified-svg/Untitled 3.svg` },
        { asset: `./svg/mercurius-2/simplified-svg/Untitled 4.svg` },
        { asset: `./svg/mercurius-2/simplified-svg/Untitled 5.svg` },
        { asset: `./svg/mercurius-2/simplified-svg/Untitled 6.svg` },
        { asset: `./svg/mercurius-2/simplified-svg/Untitled 7.svg` },
    ];

    /**
     * Get it into the light DOM, for styling from outside this component
     */
    createRenderRoot() {
        return this;
    }

    /**
     * This is a kind of game loop
     * controls are updated, a frame is requested based on a tick
     * the visualization parameter is a callback function
     * and its context is this (or subclassed method of) the component
     * 
     * It handles lag
     * @see http://www.gameprogrammingpatterns.com/game-loop.html for learning more!
     * 
     * @param visualization 
     * @param tick 
     */
    loop(visualization, tick = 0) {
        if (this.running == true) {
            tick = this.tick;
            // first measure
            this.currentTime = Date.now();
            this.elapsedTime = this.currentTime - this.previousTime;
            this.previousTime = this.currentTime;
            this.lag += this.elapsedTime;
            this.lagWarning = '';

            if (this.lag > this.TICK_RATE) {
                this.lagWarning = 'lagging';
                if (this.debugMode === true) {
                    console.info('[SvgAnimationComponent] lag detected:', this.lag, 'where tick rate is', this.TICK_RATE);
                }
            }
            if (this.debugMode === true) {
                console.log('currentTime is at', this.currentTime);
                console.log('previousTime is at', this.previousTime);
                console.log('elapsedTime is at', this.elapsedTime);
                console.log('lag is at', this.lag);
            }

            this.handleInputs();

            while (this.lag >= this.TICK_RATE) {
                this.doUpdate(visualization, tick);
                this.lag -= this.TICK_RATE;
            }

            this.doRender(visualization, tick);
        }

        requestAnimationFrame(() => {
            this.loop(visualization, tick);
        });
    }

    handleMarginLeft() {
        if (
            typeof this.frameObjects !== 'undefined' &&
            typeof this.frameObjects[this.counter] !== 'undefined' &&
            typeof this.frameObjects[this.counter].styles !== 'undefined' &&
            typeof this.frameObjects[this.counter].styles.marginLeft !== 'undefined'
        ) {
            this.marginLeft = (this.marginLeft + this.frameObjects[this.counter].styles.marginLeft);
        }
    }

    handleMarginTop() {
        if (
            typeof this.frameObjects !== 'undefined' &&
            typeof this.frameObjects[this.counter] !== 'undefined' &&
            typeof this.frameObjects[this.counter].styles !== 'undefined' &&
            typeof this.frameObjects[this.counter].styles.marginTop !== 'undefined'
        ) {
            this.marginTop = (this.marginTop + this.frameObjects[this.counter].styles.marginTop);
        }
    }

    handleTransform() {
        if (
            typeof this.frameObjects !== 'undefined' &&
            typeof this.frameObjects[this.counter] !== 'undefined' &&
            typeof this.frameObjects[this.counter].styles !== 'undefined' &&
            typeof this.frameObjects[this.counter].styles.transform !== 'undefined'
        ) {
            this.transform = (this.frameObjects[this.counter].styles.transform);
        }
    }

    /**
     * Decoupled render method
     * 
     * @param visualization 
     * @param tick 
     */
    doRender(visualize, tick) {
        this.tick += 1;
        if (this.tick % this.modulo === 0 || this.tick === 0) {
            if (this.debugMode === true){ 
                console.log('modulo!', this.tick, this.modulo);
            }
            this.handleMarginLeft();
            this.handleMarginTop();
            this.handleTransform();
            this.counter += 1;
        }
        if (this.counter === this.frameObjects.length) {
            this.counter = this.startCount;
            this.transform = 'rotateY(0deg)';
        }
        // would handle lag
        requestAnimationFrame((tick) => {
            this.requestUpdate();
            let viz = visualize.bind(this);
            viz();
        })
    }

    render() {
        let template = html`
        <div class="row"> 
            <div class="col col-auto mx-auto">
                <div class="svg-animation-component-container col-6 col-lg-2">
                    <div class="frame-counter">
                        frame: ${this.counter}
                    </div>
                    <img src="${this.styleSheetPrefix}${this.asset}" class="img-fluid svg-animation-asset" />
                </div>
            </div>
        </div>
        `;

        return template;
    }

    handleInputs() {
        if (this.debugMode === true) {
            console.log('handleInputs');
        }
        // would process input(s)
    }

    doUpdate(visualization, tick) {
        if (this.debugMode === true) {
            console.log('doUpdate...', tick);
        }
    }

    firstUpdated() {
        this.handleWrapperClick();
        this.running = true;
    }

    handleWrapperClick() {
        console.log('[SvgAnimationComponent handleWrapperClick]');
        this.loop(this.visualize);
    }

    /**
     * visualize, called about every 1/60th of a second if possible
     */
    visualize() {
        this.asset = this.frameObjects[this.counter].asset;
    }
}

//register the component with the window custom elements registry
window.customElements.define('svg-animation-component', SvgAnimationComponent);